import { BaseApiService } from './base';
import {
  SystemAnalytics,
  TaskRunStats,
  FileActivityStats,
  PartnerSummary,
  TaskSummary,
  ChartData,
  SystemOverview,
  TrendData,
  PerformanceMetrics,
  SystemAlert,
  DashboardData
} from '../../types/analytics';
import { PartnerActivityData, TaskActivityData } from '../../types/partner';
import { QueryParams } from '../../types/api';

export interface AnalyticsTimeRange {
  period: '24h' | '7d' | '30d' | '90d';
  startDate?: Date;
  endDate?: Date;
}

export interface MetricsFilters {
  partners?: string[];
  tasks?: string[];
  transferTypes?: string[];
  status?: string[];
}

export class AnalyticsApiService extends BaseApiService {
  // Dashboard data
  async getDashboardData(timeRange: AnalyticsTimeRange = { period: '24h' }): Promise<DashboardData> {
    const params: QueryParams = {
      filters: [
        { field: 'period', operator: 'eq', value: timeRange.period }
      ]
    };

    if (timeRange.startDate && timeRange.endDate) {
      params.dateRange = {
        start: timeRange.startDate,
        end: timeRange.endDate
      };
    }

    const response = await this.get('/analytics/dashboard', params);
    return response.json();
  }

  // Task statistics
  async getTaskRunStats(timeRange: AnalyticsTimeRange = { period: '24h' }): Promise<TaskRunStats> {
    const params: QueryParams = {
      filters: [
        { field: 'period', operator: 'eq', value: timeRange.period }
      ]
    };

    if (timeRange.startDate && timeRange.endDate) {
      params.dateRange = {
        start: timeRange.startDate,
        end: timeRange.endDate
      };
    }

    const response = await this.get('/analytics/task-runs', params);
    return response.json();
  }

  async getTaskSummary(): Promise<TaskSummary> {
    const response = await this.get('/analytics/task-summary');
    return response.json();
  }

  async getTaskActivityData(
    timeRange: '30d' | '90d' = '30d',
    limit: number = 10
  ): Promise<TaskActivityData[]> {
    const params: QueryParams = {
      filters: [
        { field: 'timeRange', operator: 'eq', value: timeRange },
        { field: 'limit', operator: 'eq', value: limit }
      ]
    };

    const response = await this.get('/analytics/task-activity', params);
    return response.json();
  }

  // File activity statistics
  async getFileActivityStats(timeRange: AnalyticsTimeRange = { period: '24h' }): Promise<FileActivityStats> {
    const params: QueryParams = {
      filters: [
        { field: 'period', operator: 'eq', value: timeRange.period }
      ]
    };

    if (timeRange.startDate && timeRange.endDate) {
      params.dateRange = {
        start: timeRange.startDate,
        end: timeRange.endDate
      };
    }

    const response = await this.get('/analytics/file-activity', params);
    return response.json();
  }

  // Partner statistics
  async getPartnerSummary(): Promise<PartnerSummary> {
    const response = await this.get('/analytics/partner-summary');
    return response.json();
  }

  async getPartnerActivityData(
    timeRange: '30d' | '90d' = '30d',
    limit: number = 10
  ): Promise<PartnerActivityData[]> {
    const params: QueryParams = {
      filters: [
        { field: 'timeRange', operator: 'eq', value: timeRange },
        { field: 'limit', operator: 'eq', value: limit }
      ]
    };

    const response = await this.get('/analytics/partner-activity', params);
    return response.json();
  }

  // Chart data
  async getChartData(timeRange: '30d' | '90d' = '30d'): Promise<ChartData> {
    const params: QueryParams = {
      filters: [
        { field: 'timeRange', operator: 'eq', value: timeRange }
      ]
    };

    const response = await this.get('/analytics/charts', params);
    return response.json();
  }

  // System analytics
  async getSystemAnalytics(): Promise<SystemAnalytics> {
    const response = await this.get('/analytics/system');
    return response.json();
  }

  async getSystemOverview(): Promise<SystemOverview> {
    const response = await this.get('/analytics/system-overview');
    return response.json();
  }

  // Trend data
  async getTrendData(
    metrics: string[],
    timeRange: AnalyticsTimeRange = { period: '30d' }
  ): Promise<TrendData[]> {
    const params: QueryParams = {
      filters: [
        { field: 'metrics', operator: 'in', value: metrics },
        { field: 'period', operator: 'eq', value: timeRange.period }
      ]
    };

    if (timeRange.startDate && timeRange.endDate) {
      params.dateRange = {
        start: timeRange.startDate,
        end: timeRange.endDate
      };
    }

    const response = await this.get('/analytics/trends', params);
    return response.json();
  }

  // Performance metrics
  async getPerformanceMetrics(timeRange: AnalyticsTimeRange = { period: '24h' }): Promise<PerformanceMetrics> {
    const params: QueryParams = {
      filters: [
        { field: 'period', operator: 'eq', value: timeRange.period }
      ]
    };

    if (timeRange.startDate && timeRange.endDate) {
      params.dateRange = {
        start: timeRange.startDate,
        end: timeRange.endDate
      };
    }

    const response = await this.get('/analytics/performance', params);
    return response.json();
  }

  // System alerts
  async getSystemAlerts(
    acknowledged?: boolean,
    limit: number = 50
  ): Promise<SystemAlert[]> {
    const params: QueryParams = {
      filters: [
        { field: 'limit', operator: 'eq', value: limit }
      ]
    };

    if (acknowledged !== undefined) {
      params.filters!.push({ field: 'acknowledged', operator: 'eq', value: acknowledged });
    }

    const response = await this.get('/analytics/alerts', params);
    return response.json();
  }

  async acknowledgeAlert(alertId: string): Promise<SystemAlert> {
    const response = await this.put(`/analytics/alerts/${alertId}/acknowledge`);
    return response.json();
  }

  async dismissAlert(alertId: string): Promise<void> {
    await this.delete(`/analytics/alerts/${alertId}`);
  }

  // Custom metrics
  async getCustomMetrics(
    metricNames: string[],
    timeRange: AnalyticsTimeRange,
    filters?: MetricsFilters
  ): Promise<{ [metricName: string]: TrendData[] }> {
    const params: QueryParams = {
      filters: [
        { field: 'metrics', operator: 'in', value: metricNames },
        { field: 'period', operator: 'eq', value: timeRange.period }
      ]
    };

    if (timeRange.startDate && timeRange.endDate) {
      params.dateRange = {
        start: timeRange.startDate,
        end: timeRange.endDate
      };
    }

    if (filters) {
      if (filters.partners && filters.partners.length > 0) {
        params.filters!.push({ field: 'partners', operator: 'in', value: filters.partners });
      }
      if (filters.tasks && filters.tasks.length > 0) {
        params.filters!.push({ field: 'tasks', operator: 'in', value: filters.tasks });
      }
      if (filters.transferTypes && filters.transferTypes.length > 0) {
        params.filters!.push({ field: 'transferTypes', operator: 'in', value: filters.transferTypes });
      }
      if (filters.status && filters.status.length > 0) {
        params.filters!.push({ field: 'status', operator: 'in', value: filters.status });
      }
    }

    const response = await this.get('/analytics/custom-metrics', params);
    return response.json();
  }

  // Export functionality
  async exportDashboardData(
    format: 'csv' | 'json' | 'xlsx',
    timeRange: AnalyticsTimeRange = { period: '24h' }
  ): Promise<Blob> {
    const params: QueryParams = {
      filters: [
        { field: 'format', operator: 'eq', value: format },
        { field: 'period', operator: 'eq', value: timeRange.period }
      ]
    };

    if (timeRange.startDate && timeRange.endDate) {
      params.dateRange = {
        start: timeRange.startDate,
        end: timeRange.endDate
      };
    }

    // This would return a blob for file download - using direct fetch since we need blob response
    const queryString = this.buildQueryString(params);
    const response = await fetch(`/api/analytics/export?${queryString}`);
    return response.blob();
  }
}

// Export singleton instance
export const analyticsApi = new AnalyticsApiService();